// Calculator Cache: Actions for updating & fetching Calculator Cache
import { Dispatch } from 'redux';

import { get, post, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppState } from '../reducers';
import RoutePath from '../util/mappings/routePath';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

type Action<T> = import('./types').Action<T>;
type FetchCalculatorCacheResponse = import('../generated-types').CacheLoanInfo;
type UpdateCalculatorCacheResponse = import('../generated-types').UpdateCache;

export interface FetchCalculatorCacheStartedAction extends Action<typeof actionTypes.FETCH_CALCULATOR_CACHE_STARTED> {
    type: typeof actionTypes.FETCH_CALCULATOR_CACHE_STARTED;
}

export interface FetchCalculatorCacheFulfilledAction extends Action<
    typeof actionTypes.FETCH_CALCULATOR_CACHE_FULFILLED
> {
    type: typeof actionTypes.FETCH_CALCULATOR_CACHE_FULFILLED;
    payload: FetchCalculatorCacheResponse;
}

export interface FetchCalculatorCacheFailedAction extends Action<typeof actionTypes.FETCH_CALCULATOR_CACHE_FAILED> {
    type: typeof actionTypes.FETCH_CALCULATOR_CACHE_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface UpdateCalculatorCacheStartedAction extends Action<typeof actionTypes.UPDATE_CALCULATOR_CACHE_STARTED> {
    type: typeof actionTypes.UPDATE_CALCULATOR_CACHE_STARTED;
}

export interface UpdateCalculatorCacheFulfilledAction extends Action<
    typeof actionTypes.UPDATE_CALCULATOR_CACHE_FULFILLED
> {
    type: typeof actionTypes.UPDATE_CALCULATOR_CACHE_FULFILLED;
    payload: UpdateCalculatorCacheResponse;
}

export interface UpdateCalculatorCacheFailedAction extends Action<typeof actionTypes.UPDATE_CALCULATOR_CACHE_FAILED> {
    type: typeof actionTypes.UPDATE_CALCULATOR_CACHE_FAILED;
    payload: RequestExceptionWithResponse;
}

export type CalculatorCacheActions =
    | FetchCalculatorCacheStartedAction
    | FetchCalculatorCacheFulfilledAction
    | FetchCalculatorCacheFailedAction
    | UpdateCalculatorCacheStartedAction
    | UpdateCalculatorCacheFulfilledAction
    | UpdateCalculatorCacheFailedAction;

export const fetchCalculatorCacheStarted = (): FetchCalculatorCacheStartedAction => ({
    type: actionTypes.FETCH_CALCULATOR_CACHE_STARTED,
});
export const fetchCalculatorCacheFulfilled = (
    json: FetchCalculatorCacheResponse,
): FetchCalculatorCacheFulfilledAction => ({
    type: actionTypes.FETCH_CALCULATOR_CACHE_FULFILLED,
    payload: json,
});
export const fetchCalculatorCacheFailed = (error: RequestExceptionWithResponse): FetchCalculatorCacheFailedAction => ({
    type: actionTypes.FETCH_CALCULATOR_CACHE_FAILED,
    payload: error,
});

export const updateCalculatorCacheStarted = (): UpdateCalculatorCacheStartedAction => ({
    type: actionTypes.UPDATE_CALCULATOR_CACHE_STARTED,
});
export const updateCalculatorCacheFulfilled = (
    json: UpdateCalculatorCacheResponse,
): UpdateCalculatorCacheFulfilledAction => ({
    type: actionTypes.UPDATE_CALCULATOR_CACHE_FULFILLED,
    payload: json,
});
export const updateCalculatorCacheFailed = (
    error: RequestExceptionWithResponse,
): UpdateCalculatorCacheFailedAction => ({
    type: actionTypes.UPDATE_CALCULATOR_CACHE_FAILED,
    payload: error,
});

export const fetchCalculatorCache =
    () =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { isFetchingCalculatorCache, hasFetchedCalculatorCache } = loader;

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const cacheId = urlParams.get('cacheId');

        if (cacheId && !isFetchingCalculatorCache && !hasFetchedCalculatorCache && !loader.fetchCalculatorCacheFailed) {
            try {
                dispatch(fetchCalculatorCacheStarted());
                const response: FetchCalculatorCacheResponse = await get(
                    `${actionTypes.RESOURCE_PATH}/cache/${cacheId}`,
                    getRequestHeaders(context.config.bankregnr),
                );
                dispatch(fetchCalculatorCacheFulfilled(response));
            } catch (error) {
                dispatch(fetchCalculatorCacheFailed(error));
            }
        }
    };

export const updateCalculatorCache = async (
    dispatch: Dispatch<AppActions>,
    getState: () => AppState,
): Promise<void> => {
    const { loader, calculator, context } = getState();
    const { isUpdatingCalculatorCache, hasUpdatedCalculatorCache } = loader;
    const { applicationPath } = context.config.data;
    const {
        vehicleURL,
        loanType,
        loanAmountWithoutFees,
        loanProduct,
        equity,
        requestedLoanPeriodInMonths,
        vehiclePrice,
        vehicleRegistrationNumber,
        odometerValue,
    } = calculator.loanInfo;

    const data = {
        vehicleURL,
        loanType,
        loanAmount: loanAmountWithoutFees,
        loanProduct,
        equity,
        requestedLoanPeriodInMonths,
        vehicleRegistrationNumber: vehicleRegistrationNumber || null,
        odometerValue: odometerValue !== null ? odometerValue : null,
        vehiclePrice,
    };

    if (!isUpdatingCalculatorCache && !hasUpdatedCalculatorCache) {
        try {
            dispatch(updateCalculatorCacheStarted());
            const response: UpdateCalculatorCacheResponse = await post(
                `${actionTypes.RESOURCE_PATH}/cache`,
                data,
                getRequestHeaders(context.config.bankregnr),
            );
            dispatch(updateCalculatorCacheFulfilled(response));

            if (response.cacheId) {
                window.location.href = `${applicationPath}?cacheId=${response.cacheId}/#${RoutePath.application}`;
            }
        } catch (error) {
            dispatch(updateCalculatorCacheFailed(error));
        }
    }
};
